import {
  ROUNDING_MODES,
  SERVICE_CHARGE_MODES,
  TAX_TYPES,
  type Outlet,
  type OutletDetail,
  type RoundingMode,
  type ServiceChargeMode,
  type TaxType,
} from '@/domain/entities/outlet.entity'
import type { OutletDetailDto, OutletDto } from './outlet.dto'

/** Mode asing dari jaringan jatuh ke `nearest` — default yang sama dengan BE. */
function toRoundingMode(raw: string | null | undefined): RoundingMode {
  return (ROUNDING_MODES as readonly string[]).includes(raw ?? '')
    ? (raw as RoundingMode)
    : 'nearest'
}

function toTaxType(raw: string | null | undefined, taxInclusive: boolean): TaxType {
  if (raw && (TAX_TYPES as readonly string[]).includes(raw)) {
    return raw as TaxType
  }
  return taxInclusive ? 'inclusive_after_discount' : 'exclusive_after_discount'
}

function toServiceChargeMode(raw: string | null | undefined): ServiceChargeMode {
  if (raw && (SERVICE_CHARGE_MODES as readonly string[]).includes(raw)) {
    return raw as ServiceChargeMode
  }
  return 'service_charge_is_taxed'
}

export function formatPictureUrl(url?: string | null): string {
  if (!url) return ''

  const apiBaseUrl = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api/v1').replace(
    /\/$/,
    '',
  )

  // If URL is a relative path starting with /
  if (url.startsWith('/')) {
    const rootHostUrl = apiBaseUrl.replace(/\/api\/v1\/?$/, '')
    return `${rootHostUrl}${url}`
  }

  // If URL contains r2.dev or hardcoded dev localhost, dynamically replace domain with current VITE_API_BASE_URL
  if (url.includes('r2.dev') || url.includes('localhost:8080')) {
    const keyMatch = url.match(/((?:outlets|logos|products|uploads)\/[^?#]+)/)
    if (keyMatch) {
      return `${apiBaseUrl}/media/${keyMatch[1]}`
    }
  }

  return url
}

export const outletMapper = {
  toOutlet(dto: OutletDto): Outlet {
    return {
      id: dto.id,
      name: dto.name,
      type: (dto.type as any) ?? 'store',
      active: dto.active ?? true,
      address: dto.address ?? '',
      createdAt: dto.created_at ?? undefined,
    }
  },

  /**
   * Default di sini sengaja sama dengan default BE (`POST /outlets`): outlet
   * lama yang belum punya kolom pengaturan berperilaku seperti tanpa pajak,
   * tanpa service, tanpa pembulatan — bukan seperti data rusak.
   */
  toOutletDetail(dto: OutletDetailDto): OutletDetail {
    const taxInclusive = dto.tax_inclusive ?? false
    return {
      ...outletMapper.toOutlet(dto),
      address: dto.address ?? '',
      picture: formatPictureUrl(dto.picture),
      taxType: toTaxType(dto.tax_type, taxInclusive),
      serviceChargeMode: toServiceChargeMode(dto.service_charge_mode),
      taxInclusive,
      taxValue: dto.tax_value ?? 0,
      serviceValue: dto.service_value ?? 0,
      roundingEnabled: dto.rounding_enabled ?? false,
      roundingUnit: dto.rounding_unit ?? 100,
      roundingMode: toRoundingMode(dto.rounding_mode),
      timezone: dto.timezone ?? 'Asia/Jakarta',
      businessDayCutoff: dto.business_day_cutoff ?? 0,
      kitchenEnabled: dto.kitchen_enabled ?? false,
      voidDefaultPrepared: dto.void_default_prepared ?? false,
      invoicePrefix: dto.invoice_prefix ?? 'INV',
      receiptHeader: dto.receipt_header ?? '',
      receiptFooter: dto.receipt_footer ?? '',
      receiptShowLogo: dto.receipt_show_logo ?? true,
      currency: dto.currency ?? 'IDR',
      enableTableQRStatic: dto.enable_table_qr_static ?? false,
      enableTableQRDynamic: dto.enable_table_qr_dynamic ?? false,
    }
  },
}
